import { toast } from "react-toastify"
import { exportExerciseCtfPack } from "../../../data-utils/data.js"
import { downloadBase64File } from "../../Metrics/dashboardViewsUtils"
import { useAuth0 } from "ct-auth"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { canManageChildOrgs, decodedAuth0StateFromParamString, isTrainer, isManager, isSponsor, userOrg } from "../../../data-utils/authUtils.js"
import {
  deleteSession,
  deleteSessionGroup,
  getOrg,
  reInviteUser,
  registerUser,
  setSessionArchived,
  setSessionEnded,
  updateSessionGroup,
} from "../../../data-utils/data.js"
import { useQueries } from "../../../data-utils/reactQueryHelpers.js"
import { convertToSessionsFilterParams, gameListFromExercises } from "../../../data-utils/gameSessionUtils.js"
import { encodeRFC3986URIComponent } from "../../../data-utils/uiUtils.js"
import { UiActions } from "../../../store/ui-slice"
import {
  ROUTE_PATHS,
  TABLE_GAME_ARCHIVED_FILTER,
  TABLE_GAME_FILTERS,
  TABLE_GAME_GROUP_BY_FILTER,
  TABLE_GAME_STATUS_FILTER,
  TABLE_GAME_TIME_FILTER,
} from "../../../utilities/constants"
import "./Home.scss"
import EmptyGame from "./emptyGame"
import GameList from "./gameList"
import { Chip } from "@mui/material"
import { SETUP_FILTER_LABELS, readExerciseLinkFilters, searchWithType, searchWithout } from "./exerciseLinkFilters"
import AddUsersDialog from "./AddUsersDialog.jsx"

// Modal Support
import { createPortal } from "react-dom"
import logger from "../../../Logger/index.js"
import { APP_MODAL_TYPES, MODALVIEW_CONTAINER } from "../../../utilities/constants"
import AppModal from "../../common/Modal/modal.jsx"

const pageLogger = logger.child({ module: "Exercises Home" })

function ExercisesHome() {
  const navigate = useNavigate()
  const location = useLocation()
  const { getAccessTokenSilently, user } = useAuth0()
  const dispatch = useDispatch()
  const loader = useSelector((state) => state.ui.showLoader)
  const searchText = useSelector((state) => state.gamelist.searchText)
  const filterData = useSelector((state) => state.gamelist.filters)

  const [gameList, setGameList] = useState([])
  const [allUsers, setAllUsers] = useState({}) // User cache for game list
  const [userIsSponsor, setUserIsSponsor] = useState(false) // User is a sponsor role
  const [userIsManager, setUserIsManager] = useState(false) // User is a manager role
  const [userIsTrainer, setUserIsTrainer] = useState(false) // User is an trainer role
  const [userOrgData, setUserOrgData] = useState(null) // User's org

  const [filterParam, setFilterParam] = useState("") // Filter Parameters to be sent in API calls
  // Session Filter Params in string format to preserve the last filter values for rendering loader
  const sessionFilterParams = useRef("")
  const [isFilterSearchProgress, setIsFilterSearchProgress] = useState(false)
  // List of Filters applicable to Game List Screens
  const [filtersList, setFiltersList] = useState([
    {
      filterKey: TABLE_GAME_FILTERS.ARCHIVED,
      filterLabel: "Archived Exercises",
      filterValue: filterData[TABLE_GAME_FILTERS.ARCHIVED] ?? TABLE_GAME_ARCHIVED_FILTER.HIDE,
      filterOptions: [
        {
          optionValue: TABLE_GAME_ARCHIVED_FILTER.SHOW,
          displayText: "Show",
        },
        {
          optionValue: TABLE_GAME_ARCHIVED_FILTER.HIDE,
          displayText: "Hide",
        },
      ],
      adminOnly: true,
    },
    {
      filterKey: TABLE_GAME_FILTERS.GAMES_STATUS,
      filterLabel: "Show",
      filterValue: filterData[TABLE_GAME_FILTERS.GAMES_STATUS] ?? TABLE_GAME_STATUS_FILTER.ALL,
      filterOptions: [
        {
          optionValue: TABLE_GAME_STATUS_FILTER.ALL,
          displayText: "All Exercises",
        },
        {
          optionValue: TABLE_GAME_STATUS_FILTER.ONGOING,
          displayText: "Ongoing Exercises",
        },
        {
          optionValue: TABLE_GAME_STATUS_FILTER.FINISHED,
          displayText: "Finished Exercises",
        },
        {
          optionValue: TABLE_GAME_STATUS_FILTER.PAUSED,
          displayText: "Paused Exercises",
        },
        {
          optionValue: TABLE_GAME_STATUS_FILTER.SCHEDULED,
          displayText: "Scheduled Exercises",
        },
      ],
    },
    {
      filterKey: TABLE_GAME_FILTERS.TIME,
      filterLabel: "Range",
      filterValue: filterData[TABLE_GAME_FILTERS.TIME] ?? TABLE_GAME_TIME_FILTER.ALL,
      filterOptions: [
        {
          optionValue: TABLE_GAME_TIME_FILTER.ALL,
          displayText: "All Time",
        },
        {
          optionValue: TABLE_GAME_TIME_FILTER.LAST_WEEK,
          displayText: "Last Week",
        },
        {
          optionValue: TABLE_GAME_TIME_FILTER.LAST_MONTH,
          displayText: "Last Month",
        },
        {
          optionValue: TABLE_GAME_TIME_FILTER.LAST_YEAR,
          displayText: "Last Year",
        },
        {
          optionValue: TABLE_GAME_TIME_FILTER.YEAR_TO_DATE,
          displayText: "Year to Date",
        },
      ],
    },
    {
      filterKey: TABLE_GAME_FILTERS.GROUP_BY,
      filterLabel: "Group by",
      filterValue: filterData[TABLE_GAME_FILTERS.GROUP_BY] ?? TABLE_GAME_GROUP_BY_FILTER.NONE,
      filterOptions: [
        {
          optionValue: TABLE_GAME_GROUP_BY_FILTER.NONE,
          displayText: "Default",
        },
        {
          optionValue: TABLE_GAME_GROUP_BY_FILTER.GAME_SCENARIO,
          displayText: "Exercise Scenario",
        },
        {
          optionValue: TABLE_GAME_GROUP_BY_FILTER.GAME_STATUS,
          displayText: "Exercise Status",
        },
      ],
    },
  ])

  // Show loader only on first load
  const showLoader = useRef(true)

  // Modal Support
  const [modalComponent, setmodalComponent] = useState(null)

  const appModal = createPortal(modalComponent, document.getElementById(MODALVIEW_CONTAINER))

  const handleModalClose = (_event) => {
    dispatch(UiActions.setShowModal(false))
    setmodalComponent(null)
  }

  // React Query hooks for data fetching with automatic caching and polling
  const isSponsorOrManager = useMemo(() => isSponsor(user) || isManager(user) || isTrainer(user), [user])

  // Filters a dashboard click passes in the URL (scenario, how the exercise is run).
  const linkFilters = useMemo(() => readExerciseLinkFilters(location.search), [location.search])
  const filterParams = useMemo(
    () => ({
      ...convertToSessionsFilterParams({
        filterData,
        searchInput: searchText,
      }),
      ...(linkFilters.scenario ? { test: linkFilters.scenario } : {}),
    }),
    [filterData, searchText, linkFilters.scenario]
  )
  const clearLinkFilter = (key) => navigate({ pathname: location.pathname, search: searchWithout(location.search, key) }, { replace: true })
  // CTF reports spec §6: Type = All | CTF | Training | Assessment, kept in `?type=` (shareable).
  const setTypeFilter = (type) => navigate({ pathname: location.pathname, search: searchWithType(location.search, type) }, { replace: true })

  // Fetch optimized exercises (single API call replaces 4 separate queries)
  const exercisesQuery = useQueries.HOME_EXERCISES({
    filterParams,
    enabled: !!user?.sub,
  })

  pageLogger.debug(
    {
      exercisesLoading: exercisesQuery.isLoading,
    },
    "React Query status"
  )

  // Show loader only during initial load (not during background polling/refetch)
  useEffect(() => {
    const isInitialLoad = exercisesQuery.isLoading || (exercisesQuery.isFetching && !exercisesQuery.data)
    setIsFilterSearchProgress(isInitialLoad)
  }, [exercisesQuery.isLoading, exercisesQuery.isFetching, exercisesQuery.data])

  // Process exercises query results and update local state
  useEffect(() => {
    if (exercisesQuery.isSuccess && exercisesQuery.data) {
      pageLogger.debug({ exercisesLoaded: !!exercisesQuery.data }, "Exercises data loaded successfully")

      // Track filter params change for debug logging
      const currentFilterParams = JSON.stringify(filterParams)
      if (sessionFilterParams.current !== currentFilterParams) {
        sessionFilterParams.current = currentFilterParams
        pageLogger.debug({ filterParams }, "Filter params changed")
      }

      // Transform exercises response to flat game list format
      const { gameList: mappedSessions, userCache } = gameListFromExercises(exercisesQuery.data)

      // Derive user org data from the orgs lookup in exercises response
      const userOrgId = userOrg(user)
      const orgData =
        userOrgId && exercisesQuery.data.orgs?.[userOrgId]
          ? {
              id: userOrgId,
              displayName: exercisesQuery.data.orgs[userOrgId].name,
              ...exercisesQuery.data.orgs[userOrgId],
            }
          : null

      // Update state
      setGameList(mappedSessions)
      setAllUsers(userCache)
      setUserOrgData(orgData)

      pageLogger.debug(
        { gameCount: mappedSessions.length, isSponsorOrManager },
        "Exercise list loaded - user status will be lazy-loaded per exercise"
      )

      // Hide loader after first successful load
      if (showLoader.current) {
        dispatch(UiActions.setShowLoader(false))
        showLoader.current = false
      }
    }
  }, [exercisesQuery.isSuccess, exercisesQuery.data, user, isSponsorOrManager, dispatch, filterParams])

  // Handle query errors
  useEffect(() => {
    if (exercisesQuery.isError) {
      pageLogger.error({ exercisesError: exercisesQuery.error }, "Exercises query error occurred")

      // Hide loader on error
      if (showLoader.current) {
        dispatch(UiActions.setShowLoader(false))
        showLoader.current = false
      }
    }
  }, [exercisesQuery.isError, exercisesQuery.error, dispatch])

  // Manual refetch function for handlers to trigger data refresh
  const refetchGameData = useCallback(() => {
    pageLogger.debug("Manual refetch triggered")
    exercisesQuery.refetch()
  }, [exercisesQuery.refetch])

  // Add-users dialog: group root -> add participants; session -> add a player.
  const [addUsersDialog, setAddUsersDialog] = useState({ open: false, mode: "group", target: null, org: null })
  const handleAddUsers = useCallback(({ target, org = null, isGroupRoot }) => {
    setAddUsersDialog({ open: true, mode: isGroupRoot ? "group" : "session", target, org })
  }, [])
  const handleAddUsersClose = useCallback(() => {
    setAddUsersDialog((prev) => ({ ...prev, open: false }))
  }, [])

  const inviteConfirmationModelProps = () => ({
    modalType: APP_MODAL_TYPES.INFO,
    handleModalClose: handleModalClose,
    modalBodyClass: ["info-modal-body-class"],
    modalData: {
      title: "Invitation Sent",
      subTitle: "Your invitation was sent!",
      actionButtons: [
        {
          text: "Close",
          classNames: ["btn-primary", "btn-filled"],
          clickHandler: handleModalClose,
        },
      ],
    },
  })

  const inviteFailedModelProps = () => ({
    modalType: APP_MODAL_TYPES.ERROR,
    handleModalClose: handleModalClose,
    modalBodyClass: ["info-modal-body-class"],
    modalData: {
      title: "No Invitation Sent",
      subTitle: "No invitation was sent. It's not this player's turn or the exercise may no longer be in progress.",
      actionButtons: [
        {
          text: "Close",
          classNames: ["btn-primary", "btn-filled"],
          clickHandler: handleModalClose,
        },
      ],
    },
  })

  const openInviteConfirmationModal = () => {
    dispatch(UiActions.setShowModal(true))
    setmodalComponent(React.createElement(AppModal, inviteConfirmationModelProps()))
  }

  const openFailedInviteModal = () => {
    dispatch(UiActions.setShowModal(true))
    setmodalComponent(React.createElement(AppModal, inviteFailedModelProps()))
  }

  /**
   * Function to handle sending an invitation to a player/sponsor
   * @param {String} game - the id (name) of the game / session
   * @param {String} email - the email address of the player receiving the invitation
   */
  // Dynamic-question CTF: download one exercise's facilitator pack (its generated set) once it's ready.
  const handleDownloadCtfPack = useCallback(
    async ({ name }) => {
      try {
        downloadBase64File(await exportExerciseCtfPack(await getAccessTokenSilently(), user?.sub, { name }))
      } catch (e) {
        toast.error(e?.serverMessage || e?.message || "Couldn't build the facilitator pack — please try again.")
      }
    },
    [getAccessTokenSilently, user?.sub]
  )
  const handleSendInvite = useCallback(
    ({ game, email }) => {
      const sendInvitation = async () => {
        handleModalClose()
        const accessToken = await getAccessTokenSilently()
        const res = await reInviteUser(accessToken, user?.sub, {
          session: game,
          email,
        })
        pageLogger.info({ game, invitingUser: email, invitationResponse: res }, `Exercise Invitation`)
        if (res === false) {
          openFailedInviteModal()
        } else {
          openInviteConfirmationModal()
        }
      }
      sendInvitation()
    },
    [getAccessTokenSilently, user?.sub]
  )

  /**
   * Generic are-you-sure modal for archive/delete style actions (per product rule:
   * every archive/delete asks first).
   */
  const openConfirmModal = useCallback(
    ({ title, subTitle, confirmText, onConfirm }) => {
      dispatch(UiActions.setShowModal(true))
      setmodalComponent(
        React.createElement(AppModal, {
          modalType: APP_MODAL_TYPES.ERROR,
          handleModalClose: handleModalClose,
          modalBodyClass: ["info-modal-body-class"],
          modalData: {
            title,
            subTitle,
            actionButtons: [
              { text: "Cancel", classNames: ["btn-primary"], clickHandler: handleModalClose },
              { text: confirmText, classNames: ["btn-primary", "btn-filled"], clickHandler: onConfirm },
            ],
          },
        })
      )
    },
    [dispatch]
  )

  /**
   * Permanent exercise deletion (single or group), behind a destructive confirm.
   * The confirm spells out the blast radius (group = all member sessions; reports
   * are lost) and suggests Archive as the reversible alternative. Failures show the
   * server's reason verbatim (e.g. "part of the group exercise ... delete the group
   * instead").
   */
  const runDeleteExercise = useCallback(
    ({ game, org, isGroupRoot }) => {
      const doDelete = async () => {
        handleModalClose()
        dispatch(UiActions.setShowLoader(true))
        try {
          const accessToken = await getAccessTokenSilently()
          if (isGroupRoot) {
            await deleteSessionGroup(accessToken, user?.sub, { name: game, org })
          } else {
            await deleteSession(accessToken, user?.sub, { name: game, org })
          }
          pageLogger.info({ game, isGroupRoot }, "Exercise deleted")
          refetchGameData()
        } catch (error) {
          pageLogger.error({ game, error }, "Exercise delete failed")
          dispatch(UiActions.setShowModal(true))
          setmodalComponent(
            React.createElement(AppModal, {
              modalType: APP_MODAL_TYPES.ERROR,
              handleModalClose: handleModalClose,
              modalBodyClass: ["info-modal-body-class"],
              modalData: {
                title: "Exercise Not Deleted",
                subTitle: error?.message || "The exercise could not be deleted.",
                actionButtons: [{ text: "Close", classNames: ["btn-primary", "btn-filled"], clickHandler: handleModalClose }],
              },
            })
          )
        } finally {
          dispatch(UiActions.setShowLoader(false))
        }
      }
      doDelete()
    },
    [dispatch, getAccessTokenSilently, user?.sub, refetchGameData]
  )

  const handleDeleteGame = useCallback(
    ({ game, org = null, isGroupRoot = false }) => {
      dispatch(UiActions.setShowModal(true))
      setmodalComponent(
        React.createElement(AppModal, {
          modalType: APP_MODAL_TYPES.ERROR,
          handleModalClose: handleModalClose,
          modalBodyClass: ["info-modal-body-class"],
          modalData: {
            title: `Delete '${game}'?`,
            subTitle: isGroupRoot
              ? "This permanently deletes the group exercise, ALL of its member sessions, and their results and reports. This cannot be undone. If you may need the history, use Archive instead."
              : "This permanently deletes the exercise and its results and reports. This cannot be undone. If you may need the history, use Archive instead.",
            actionButtons: [
              { text: "Cancel", classNames: ["btn-primary"], clickHandler: handleModalClose },
              {
                text: "Delete permanently",
                classNames: ["btn-primary", "btn-filled"],
                clickHandler: () => runDeleteExercise({ game, org, isGroupRoot }),
              },
            ],
          },
        })
      )
    },
    [dispatch, runDeleteExercise]
  )

  /**
   * Function to handle archiving a game / session
   * @param {String} game - the id (name) of the game / session
   */
  const runArchiveGame = useCallback(
    ({ game }) => {
      const archiveGame = async () => {
        handleModalClose()
        const accessToken = await getAccessTokenSilently()
        const res = await setSessionArchived(accessToken, user?.sub, {
          name: game,
          archived: true,
        })
        pageLogger.info({ game, response: res }, "Archive Game Response")
        refetchGameData()
      }
      archiveGame()
    },
    [getAccessTokenSilently, user?.sub, refetchGameData]
  )

  // Archive asks first (unarchive stays immediate — it restores visibility, nothing is
  // hidden or lost by it).
  const handleArchiveGame = useCallback(
    ({ game }) => {
      openConfirmModal({
        title: `Archive '${game}'?`,
        subTitle:
          "The exercise will be hidden from the everyday list; its results and reports are kept. You can unarchive it any time via the Archived filter.",
        confirmText: "Archive",
        onConfirm: () => runArchiveGame({ game }),
      })
    },
    [openConfirmModal, runArchiveGame]
  )

  /**
   * Function to handle archiving a group game
   * @param {String} game - the id (name) of the group game
   * @param {String} org - the organization (id) of the group game
   */
  const runArchiveGroupGame = useCallback(
    ({ game, org = null }) => {
      const archiveGroup = async () => {
        handleModalClose()
        const accessToken = await getAccessTokenSilently()
        const res = await updateSessionGroup(accessToken, user?.sub, {
          name: game,
          archived: true,
          org,
        })
        pageLogger.info({ game, response: res }, "Archiving Group Game")
        refetchGameData()
      }
      archiveGroup()
    },
    [getAccessTokenSilently, user?.sub, refetchGameData]
  )

  const handleArchiveGroupGame = useCallback(
    ({ game, org = null }) => {
      openConfirmModal({
        title: `Archive '${game}'?`,
        subTitle:
          "The group exercise (and its member sessions) will be hidden from the everyday list; results and reports are kept. You can unarchive it any time.",
        confirmText: "Archive",
        onConfirm: () => runArchiveGroupGame({ game, org }),
      })
    },
    [openConfirmModal, runArchiveGroupGame]
  )

  /**
   * Function to handle marking group game completed
   * @param {String} game - the id (name) of the group game
   * @param {String} org - the organization (id) of the group game
   */
  const handleMarkGroupGameFinished = useCallback(
    ({ game, org = null }) => {
      const markGroupFinished = async () => {
        handleModalClose()
        const accessToken = await getAccessTokenSilently()
        const res = await updateSessionGroup(accessToken, user?.sub, {
          name: game,
          ended: true,
          org,
        })
        pageLogger.info({ game, response: res }, "Marking Group Game Completed")
        refetchGameData()
      }
      markGroupFinished()
    },
    [getAccessTokenSilently, user?.sub, refetchGameData]
  )

  /**
   * Function to handle un-marking group game completed
   * @param {String} game - the id (name) of the group game
   * @param {String} org - the organization (id) of the group game
   */
  const handleUnmarkGroupGameFinished = useCallback(
    ({ game, org = null }) => {
      const unmarkGroupFinished = async () => {
        handleModalClose()
        const accessToken = await getAccessTokenSilently()
        const res = await updateSessionGroup(accessToken, user?.sub, {
          name: game,
          ended: false,
          org,
        })
        pageLogger.info({ game, response: res }, "Unmarking Group Game Completed")
        refetchGameData()
      }
      unmarkGroupFinished()
    },
    [getAccessTokenSilently, user?.sub, refetchGameData]
  )

  /**
   * Function to handle un-archiving a game / session
   * @param {String} game - the id (name) of the game / session
   */
  const handleUnarchiveGame = useCallback(
    ({ game }) => {
      const unarchiveGame = async () => {
        handleModalClose()
        const accessToken = await getAccessTokenSilently()
        const res = await setSessionArchived(accessToken, user?.sub, {
          name: game,
          archived: false,
        })
        pageLogger.info({ game, response: res }, "Unarchiving Game")
        refetchGameData()
      }
      unarchiveGame()
    },
    [getAccessTokenSilently, user?.sub, refetchGameData]
  )

  /**
   * Function to handle un-archiving a group game
   * @param {String} game - the id (name) of the group game
   * @param {String} org - the organization (id) of the group game
   */
  const handleUnarchiveGroupGame = useCallback(
    ({ game, org = null }) => {
      const unarchiveGroup = async () => {
        handleModalClose()
        const accessToken = await getAccessTokenSilently()
        const res = await updateSessionGroup(accessToken, user?.sub, {
          name: game,
          archived: false,
          org,
        })
        pageLogger.info({ game, response: res }, "Unarchiving group Game")
        refetchGameData()
      }
      unarchiveGroup()
    },
    [getAccessTokenSilently, user?.sub, refetchGameData]
  )

  /**
   * Function to handle marking a game / session finished
   * @param {String} game - the id (name) of the game / session
   */
  const handleMarkGameFinished = useCallback(
    ({ game }) => {
      const finishGame = async () => {
        handleModalClose()
        const accessToken = await getAccessTokenSilently()
        const res = await setSessionEnded(accessToken, user?.sub, {
          name: game,
          ended: true,
        })
        pageLogger.info({ game, response: res }, "Marking Game Finished")
        refetchGameData()
      }
      finishGame()
    },
    [getAccessTokenSilently, user?.sub, refetchGameData]
  )

  /**
   * Function to handle marking unmarking game / session finished
   * @param {String} game - the id (name) of the game / session
   */
  const handleUnmarkGameFinished = useCallback(
    ({ game }) => {
      const unmarkGameFinished = async () => {
        handleModalClose()
        const accessToken = await getAccessTokenSilently()
        const res = await setSessionEnded(accessToken, user?.sub, {
          name: game,
          ended: false,
        })
        pageLogger.info({ game, response: res }, "Unmarking Game Finished")
        refetchGameData()
      }
      unmarkGameFinished()
    },
    [getAccessTokenSilently, user?.sub, refetchGameData]
  )

  useEffect(() => {
    // Check for 'game' id in the URL (from an Auth0 invite link)
    const appState = decodedAuth0StateFromParamString(location?.hash)
    if (appState?.game) {
      // Navigate directly to the game
      navigate(ROUTE_PATHS.GAME_ROOT + `/${encodeRFC3986URIComponent(appState.game)}`)
    }

    if (user?.sub) {
      // Use API data
      const userSetup = async () => {
        const needsCompanyDetailsCheck = isSponsor(user)
        const accessToken = await getAccessTokenSilently()
        // Register User metadata with back-end
        // Register user's metadata
        await registerUser(accessToken, user.sub, {
          email: user.email,
          picture: user.picture,
          name: user.name,
        })
        if (needsCompanyDetailsCheck) {
          const orgId = userOrg(user)
          if (orgId) {
            const org = await getOrg(accessToken, user.sub, { id: orgId })
            // redirect to the company details screen
            if (org?.configured === false) {
              // Navigate directly to the game
              navigate(ROUTE_PATHS.COMPANY_DETAILS)
            }
          }
        }
      }
      userSetup()
      setUserIsSponsor(isSponsor(user))
      setUserIsManager(isManager(user))
      setUserIsTrainer(isTrainer(user))
    } else {
      setUserIsSponsor(true)
    }
  }, [user, getAccessTokenSilently, location, navigate, dispatch])

  useEffect(() => {
    dispatch(UiActions.setIsLeftPanelVisible(true))

    let filterParam = ""
    Object.keys(filterData).forEach((filterKey) => {
      if (filterData[filterKey] && filterData[filterKey] !== "All")
        filterParam += `${filterParam.length > 0 ? "&" : ""}${filterKey}=${filterData[filterKey]}`
    })
    setFilterParam(filterParam)
    // Also update the availableFilters list
    const updatedFilterList = filtersList.map((filterEle) => {
      return {
        ...filterEle,
        filterValue: filterData[filterEle.filterKey],
      }
    })
    setFiltersList(updatedFilterList)

    return () => {
      dispatch(UiActions.setIsLeftPanelVisible(false))
    }

    // Do not include filtersList in dependency array below
    // as it will result in recursive page load
  }, [filterData, dispatch])

  const hasLinkFilters = !!(linkFilters.scenario || linkFilters.setup)
  const showGameList = user?.sub && gameList && (gameList?.length > 0 || filterParam || searchText || hasLinkFilters)
  const linkScenarioName = linkFilters.scenario
    ? (gameList.find((g) => g.game_scenario?.scenario_id === linkFilters.scenario)?.game_scenario?.scenario_name ?? linkFilters.scenario)
    : null

  return (
    <>
      {!loader && (
        <>
          <div className="game-home">
            {showGameList ? (
              <GameList
                gameListData={gameList}
                searchText={searchText ?? ""}
                filters={filtersList}
                filterData={filterData}
                filterSearchProgress={isFilterSearchProgress}
                userData={allUsers}
                sponsorView={userIsSponsor || userIsTrainer}
                managerView={userIsManager}
                showOrgColumn={canManageChildOrgs(user)}
                orgData={userOrgData}
                authUser={user}
                handleSendInvite={handleSendInvite}
                handleDownloadCtfPack={handleDownloadCtfPack}
                handleArchiveGame={handleArchiveGame}
                handleUnarchiveGame={handleUnarchiveGame}
                handleMarkGameFinished={handleMarkGameFinished}
                handleUnmarkGameFinished={handleUnmarkGameFinished}
                handleArchiveGroupGame={handleArchiveGroupGame}
                handleUnarchiveGroupGame={handleUnarchiveGroupGame}
                handleMarkGroupGameFinished={handleMarkGroupGameFinished}
                handleUnmarkGroupGameFinished={handleUnmarkGroupGameFinished}
                handleAddUsers={handleAddUsers}
                handleDeleteGame={handleDeleteGame}
                setupFilter={linkFilters.setup}
                typeFilter={linkFilters.type}
                onTypeFilterChange={setTypeFilter}
                filterChips={
                  hasLinkFilters ? (
                    <div className="ct-exercises-link-filters" role="status" aria-label="Filters from the dashboard">
                      <span className="muted">{"Showing:"}</span>
                      {linkFilters.scenario ? (
                        <Chip size="small" label={`Scenario: ${linkScenarioName}`} onDelete={() => clearLinkFilter("scenario")} />
                      ) : null}
                      {linkFilters.setup ? (
                        <Chip
                          size="small"
                          label={`How it's run: ${SETUP_FILTER_LABELS[linkFilters.setup]}`}
                          onDelete={() => clearLinkFilter("setup")}
                        />
                      ) : null}
                    </div>
                  ) : null
                }
              />
            ) : (
              <EmptyGame sponsorView={userIsSponsor || userIsTrainer} />
            )}
          </div>
          {/* MODAL CONTENT */}
          {appModal}
        </>
      )}
      {/* Add-users dialog renders regardless of the list loader so it can't be
          unmounted mid-interaction. */}
      <AddUsersDialog
        open={addUsersDialog.open}
        mode={addUsersDialog.mode}
        targetName={addUsersDialog.target}
        org={addUsersDialog.org}
        onClose={handleAddUsersClose}
        onAdded={refetchGameData}
      />
    </>
  )
}

export default ExercisesHome
